import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type PropsValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';

import { useReactSelectValueAsOptions } from './useReactSelectValueAsOptions';
import { QUERY_STALE_TIME, SEARCH_DEBOUNCE_TIME } from '../constants';

interface QueryFnParams {
  ids?: Array<number>;
  searchText?: string;
}

interface UseAdaptiveReactSelectProps<TQueryFnResponse, TData extends object> {
  dataToOptions: (data: Array<TData>) => Array<OptionType>;
  debounceTime?: number;
  extractData: (response: TQueryFnResponse) => Array<TData>;
  isMulti?: boolean;
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnResponse>;
  queryKey: Array<unknown>;
  shouldUseAsync?: (defaultOptionsQueryResponse: TQueryFnResponse) => boolean;
  staleTime?: number;
  value?:
    | Array<number>
    | Array<TData>
    | PropsValue<OptionType>
    | TData
    | number
    | null;
}

interface UseAdaptiveReactSelectReturn {
  defaultOptions: Array<OptionType>;
  isAsync: boolean;
  isLoading: boolean;
  loadOptions: (textSearchValue: string) => Promise<Array<OptionType>>;
  value: PropsValue<OptionType>;
}

export const useAdaptiveReactSelect = <TQueryFnData, TData extends object>({
  queryKey,
  queryFn,
  extractData,
  shouldUseAsync,
  dataToOptions,
  isMulti,
  value,
  staleTime = QUERY_STALE_TIME,
  debounceTime = SEARCH_DEBOUNCE_TIME,
}: UseAdaptiveReactSelectProps<
  TQueryFnData,
  TData
>): UseAdaptiveReactSelectReturn => {
  const queryClient = useQueryClient();

  const [isAsync, setIsAsync] = useState(false);

  // Load the default options
  // IF all the existing options fit in a single page of data, the component will act as a normal select
  //    and filtering will thus be applied on the frontend side (as per react-select)
  // IF there are more items than the data loaded, we switch to async mode to allow the user to search
  //    and retrieve any item by making additional calls with the searchText
  const { data: defaultOptionsData, isLoading: isLoadingDefaultOptions } =
    useQuery({
      queryKey,
      queryFn: async ({ signal }) => {
        const response = await queryFn({}, signal);

        const data = extractData(response);
        const matchedOptions = dataToOptions(data);

        return {
          options: matchedOptions,
          defaultOptionsQueryResponse: response,
        };
      },

      staleTime,
    });

  const defaultOptions = useMemo(
    () => defaultOptionsData?.options ?? [],
    [defaultOptionsData?.options],
  );
  const defaultOptionsQueryResponse =
    defaultOptionsData?.defaultOptionsQueryResponse;

  const {
    isLoading: isLoadingValueAsOptions,
    valueAsOptions,
    addOptionsToCache,
  } = useReactSelectValueAsOptions({
    queryKey,
    value,
    defaultOptions,
    enabled: !isLoadingDefaultOptions,
    queryFn,
    extractData,
    dataToOptions,
    staleTime,
  });

  // Since queryFn is NOT called when cached data is available, we must do this processing
  // outside of the queryFn
  useEffect(() => {
    if (defaultOptionsQueryResponse && shouldUseAsync) {
      setIsAsync(shouldUseAsync(defaultOptionsQueryResponse));
    } else {
      setIsAsync(false);
    }
  }, [defaultOptionsQueryResponse, shouldUseAsync]);

  const inputChangeAbortController = useRef<AbortController | null>(null);
  const handleInputChange = useCallback(
    (textSearchValue: string) => {
      inputChangeAbortController.current?.abort();
      inputChangeAbortController.current = new AbortController();

      return new Promise<Array<OptionType>>((resolve, reject) => {
        const timeoutId = setTimeout(async () => {
          try {
            // react-select caching is not playing well with our "debounced" approach
            // so we disable it (cacheOptions={false}) and use react-query's caching
            const response = await queryClient.fetchQuery({
              queryKey: [...queryKey, textSearchValue],
              queryFn: ({ signal }) => {
                // Create a combined abort controller for backward compatibility
                // since AbortSignal.any() is not available in older browsers
                const combinedAbortController = new AbortController();

                const signals = [
                  signal,
                  inputChangeAbortController.current?.signal,
                ].filter(Boolean);

                signals.forEach((abortSignal) => {
                  if (abortSignal?.aborted) {
                    combinedAbortController.abort();

                    return;
                  }

                  abortSignal?.addEventListener('abort', () => {
                    combinedAbortController.abort();
                  });
                });

                return queryFn(
                  { searchText: textSearchValue },
                  combinedAbortController.signal,
                );
              },
              staleTime,
            });

            const data = extractData(response);
            const matchedOptions = dataToOptions(data);

            addOptionsToCache(matchedOptions);

            resolve(matchedOptions);
          } catch (error) {
            reject(error);
          }
        }, debounceTime);

        inputChangeAbortController.current?.signal.addEventListener(
          'abort',
          () => {
            reject(new Error('Search aborted'));
            clearTimeout(timeoutId);
          },
        );
      });
    },
    [
      queryClient,
      queryKey,
      queryFn,
      extractData,
      dataToOptions,
      addOptionsToCache,
      staleTime,
      debounceTime,
    ],
  );

  const valueToUse = useMemo(() => {
    if (!valueAsOptions) {
      return null;
    }

    if (Array.isArray(valueAsOptions) && !isMulti) {
      return valueAsOptions[0] ?? null;
    }

    return valueAsOptions ?? null;
  }, [isMulti, valueAsOptions]);

  return {
    defaultOptions,
    isAsync,
    isLoading: isLoadingValueAsOptions || isLoadingDefaultOptions,
    loadOptions: handleInputChange,
    value: valueToUse,
  };
};
